import { WEB_MAKER_TABS } from "../constants";

export const WEB_MAKER_ROUTE_BASE = "/web-maker";
export const WEB_MAKER_EXECUTABLE_ROUTE_PATH = `${WEB_MAKER_ROUTE_BASE}/*`;

export const WEB_MAKER_ROUTE_PAGES = Object.freeze([
  WEB_MAKER_TABS.PAGES,
  WEB_MAKER_TABS.PREVIEW,
  WEB_MAKER_TABS.SETTINGS,
  WEB_MAKER_TABS.THEME,
  WEB_MAKER_TABS.SECTIONS,
  WEB_MAKER_TABS.CATALOG,
]);

const PAGE_TO_SEGMENT = Object.freeze({
  [WEB_MAKER_TABS.PAGES]: "pages",
  [WEB_MAKER_TABS.PREVIEW]: "preview",
  [WEB_MAKER_TABS.SETTINGS]: "settings",
  [WEB_MAKER_TABS.THEME]: "themes",
  [WEB_MAKER_TABS.SECTIONS]: "sections",
  [WEB_MAKER_TABS.CATALOG]: "components",
});

const SEGMENT_TO_PAGE = Object.freeze({
  pages: WEB_MAKER_TABS.PAGES,
  preview: WEB_MAKER_TABS.PREVIEW,
  website: WEB_MAKER_TABS.PREVIEW,
  settings: WEB_MAKER_TABS.SETTINGS,
  theme: WEB_MAKER_TABS.THEME,
  themes: WEB_MAKER_TABS.THEME,
  section: WEB_MAKER_TABS.SECTIONS,
  sections: WEB_MAKER_TABS.SECTIONS,
  catalog: WEB_MAKER_TABS.CATALOG,
  components: WEB_MAKER_TABS.CATALOG,
});

export const getWebMakerRoutePath = (page) => {
  const segment = PAGE_TO_SEGMENT[page] || PAGE_TO_SEGMENT[WEB_MAKER_TABS.PREVIEW];
  return `${WEB_MAKER_ROUTE_BASE}/${segment}`;
};

export const isSuperAdminWebMakerPage = (page) =>
  page === WEB_MAKER_TABS.CATALOG || page === WEB_MAKER_TABS.SECTIONS;

export const WEB_MAKER_NAVIGATION_ITEMS = Object.freeze(
  WEB_MAKER_ROUTE_PAGES.map((page) =>
    Object.freeze({
      page,
      path: getWebMakerRoutePath(page),
      superAdminOnly: isSuperAdminWebMakerPage(page),
    })
  )
);

export const filterSuperAdminWebMakerSidebarViews = (views) =>
  (Array.isArray(views) ? views : []).filter(
    (view) => view?.webMakerNavigation || view?.webMakerRoute
  );

export const resolveAllowedWebMakerPage = (page, isSuperAdminStandalone) => {
  const normalizedPage = WEB_MAKER_ROUTE_PAGES.includes(page)
    ? page
    : WEB_MAKER_TABS.PREVIEW;
  if (isSuperAdminWebMakerPage(normalizedPage) && !isSuperAdminStandalone) {
    return WEB_MAKER_TABS.PREVIEW;
  }
  return normalizedPage;
};

/**
 * Parse legacy/current paths and return the canonical page URL.
 * `/web-maker`, aliases and unknown descendants all safely fall back to Preview.
 */
export const parseWebMakerRoute = (pathname) => {
  const normalizedPath = `/${String(pathname || "")
    .split("?")[0]
    .split("#")[0]
    .split("/")
    .filter(Boolean)
    .join("/")}`;
  const segments = normalizedPath.split("/").filter(Boolean);
  const isWebMakerPath = segments[0] === "web-maker";
  const requestedSegment = isWebMakerPath && segments.length === 2 ? segments[1] : "";
  const page = SEGMENT_TO_PAGE[requestedSegment] || WEB_MAKER_TABS.PREVIEW;
  const canonicalPath = getWebMakerRoutePath(page);

  return {
    page,
    canonicalPath,
    isCanonical: normalizedPath === canonicalPath,
  };
};

export const mapWebMakerPageToTabState = (page, isSuperAdminStandalone) => {
  const allowedPage = resolveAllowedWebMakerPage(page, isSuperAdminStandalone);

  if (isSuperAdminStandalone) {
    if (allowedPage === WEB_MAKER_TABS.THEME) {
      return {
        activeTab: WEB_MAKER_TABS.THEME,
        activePreviewSubTab: WEB_MAKER_TABS.PREVIEW,
      };
    }
    if (
      allowedPage === WEB_MAKER_TABS.CATALOG ||
      allowedPage === WEB_MAKER_TABS.SECTIONS
    ) {
      return {
        activeTab: allowedPage,
        activePreviewSubTab: WEB_MAKER_TABS.PREVIEW,
      };
    }
    return {
      activeTab: WEB_MAKER_TABS.WEBSITE,
      activePreviewSubTab: allowedPage,
    };
  }

  return {
    activeTab:
      allowedPage === WEB_MAKER_TABS.PAGES ||
      allowedPage === WEB_MAKER_TABS.SETTINGS ||
      allowedPage === WEB_MAKER_TABS.THEME
        ? allowedPage
        : WEB_MAKER_TABS.WEBSITE,
    activePreviewSubTab: WEB_MAKER_TABS.PREVIEW,
  };
};
